import React, { useState, useEffect } from 'react';
import { FileText, Image as ImageIcon, Download, Trash2, Eye, Calendar, Filter, Upload } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { getConfig } from '../../../utils/config';

const API_URL_BASE = getConfig('API_URL');
const cleanUrl = API_URL_BASE.endsWith('/') ? API_URL_BASE.slice(0, -1) : API_URL_BASE;
const API_URL = cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`;

function Documents() {
    const { theme } = useTheme();
    const { token } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all'); // all, images, documents, generated, uploaded

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            // Using apiGet with automatic token refresh
            const { apiGet } = await import('../../../utils/api');
            const response = await apiGet('/documents');

            if (!response.ok) {
                throw new Error(`Failed to fetch documents: ${response.statusText}`);
            }

            const result = await response.json();
            setDocuments(result.documents || []);
        } catch (err) {
            console.error('Error fetching documents:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteDocument = async (documentId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        try {
            // Using apiDelete with automatic token refresh
            const { apiDelete } = await import('../../../utils/api');
            const response = await apiDelete(`/documents/${documentId}`);

            if (!response.ok) {
                throw new Error('Failed to delete document');
            }

            setDocuments(prev => prev.filter(doc => doc.id !== documentId));
        } catch (err) {
            console.error('Error deleting document:', err);
            setError(err.message);
        }
    };

    const downloadDocument = async (document) => {
        try {
            // Using apiGet with automatic token refresh
            const { apiGet } = await import('../../../utils/api');
            const response = await apiGet(`/documents/${document.id}/download`);

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Get the filename from response headers or use document title
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `${document.title}.${getFileExtension(document.file_type)}`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Download failed:', error);
            // Fallback to view in new tab
            viewDocument(document);
        }
    };

    const getFileExtension = (fileType) => {
        const extensions = {
            'PNG': 'png',
            'JPG': 'jpg', 
            'JPEG': 'jpeg',
            'GIF': 'gif',
            'PDF': 'pdf',
            'TXT': 'txt',
            'DOC': 'doc',
            'DOCX': 'docx',
            'XLS': 'xls',
            'XLSX': 'xlsx'
        };
        return extensions[fileType?.toUpperCase()] || fileType?.toLowerCase() || 'file';
    };

    const viewDocument = (document) => {
        if (document.download_url) {
            window.open(document.download_url, '_blank');
        }
    };

    const filteredDocuments = documents.filter(doc => {
        switch (filter) {
            case 'images':
                return doc.is_image;
            case 'documents':
                return !doc.is_image;
            case 'generated':
                return doc.is_generated;
            case 'uploaded':
                return !doc.is_generated;
            default:
                return true;
        }
    });

    const getFileIcon = (document) => {
        if (document.is_image) {
            return <ImageIcon className="w-5 h-5 text-purple-500" />;
        }
        return <FileText className="w-5 h-5 text-blue-500" />;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className={`p-6 h-full overflow-y-auto max-w-screen-2xl mx-auto ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
                <div className="space-y-6">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="h-32 bg-gray-300 rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`p-6 h-full max-w-screen-2xl mx-auto ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            <div className="h-full flex flex-col">
                {/* Header */}
                <div className="mb-8 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <FileText className="w-8 h-8 text-blue-500" />
                            <div>
                                <h1 className="font-unna text-3xl font-bold">Documents</h1>
                                <p className={`font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Manage your uploaded files and generated images
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Filter className="w-5 h-5" />
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className={`px-3 font-rasa py-2 rounded-lg border ${
                                    theme === 'dark' 
                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                    : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            >
                                <option value="all">All Files</option>
                                <option value="images">Images Only</option>
                                <option value="documents">Documents Only</option>
                                <option value="generated">AI Generated</option>
                                <option value="uploaded">Uploaded</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mb-6 p-4 rounded-lg bg-red-100 border border-red-300 text-red-700 flex-shrink-0">
                        {error}
                    </div>
                )}

                {/* Documents Grid - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                    {filteredDocuments.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-6">
                        {filteredDocuments.map((document) => (
                            <div
                                key={document.id}
                                className={`rounded-lg border transition-all duration-200 hover:shadow-lg ${
                                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                }`}
                            >
                                {/* Preview */}
                                <div className={`h-32 rounded-t-lg overflow-hidden ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                                }`}>
                                    {document.is_image && document.download_url ? (
                                        <img
                                            src={document.download_url}
                                            alt={document.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            {getFileIcon(document)}
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <h3 className="font-medium text-sm line-clamp-2" title={document.title}>
                                                {document.title}
                                            </h3>
                                            <div className="flex items-center space-x-2 mt-1">
                                                <span className={`text-xs px-2 py-1 rounded ${
                                                    document.is_image 
                                                    ? 'bg-purple-100 text-purple-700' 
                                                    : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {document.file_type}
                                                </span>
                                                {document.is_generated && (
                                                    <span className="text-xs px-2 py-1 font-rasa rounded bg-green-100 text-green-700">
                                                        JPRO Generated
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`text-xs mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <div className="flex items-center space-x-1">
                                            <Calendar className="w-3 h-3" />
                                            <span>{formatDate(document.created_at)}</span>
                                        </div>
                                        <div className="mt-1">
                                            Size: {document.file_size}
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {document.description && (
                                        <p className={`text-xs mb-3 line-clamp-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                        }`}>
                                            {document.description}
                                        </p>
                                    )}

                                    {/* Actions */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex space-x-2">
                                            {document.download_url && (
                                                <button
                                                    onClick={() => viewDocument(document)}
                                                    className={`p-2 rounded transition-colors ${
                                                        theme === 'dark' 
                                                        ? 'bg-gray-700 hover:bg-gray-600' 
                                                        : 'bg-gray-100 hover:bg-gray-200'
                                                    }`}
                                                    title="View in new tab"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => downloadDocument(document)}
                                                className={`p-2 rounded transition-colors ${
                                                    theme === 'dark' 
                                                    ? 'bg-gray-700 hover:bg-gray-600' 
                                                    : 'bg-gray-100 hover:bg-gray-200'
                                                }`}
                                                title="Download file"
                                            >
                                                <Download className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => deleteDocument(document.id)}
                                            className="p-2 rounded text-red-500 hover:bg-red-100 transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        </div>
                    ) : (
                        <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg font-rasa">No documents found</p>
                            <p className="text-sm">
                                {filter === 'all' 
                                    ? 'Upload files or generate images to see them here'
                                    : `No ${filter.replace('_', ' ')} found`}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Documents;